import { learningDay } from "./activity";

export interface ActivityClock {
  monotonic: () => number;
  wallTime: () => number;
}
export const browserActivityClock: ActivityClock = {
  monotonic: () => performance.now(),
  wallTime: () => Date.now(),
};
export const ACTIVITY_IDLE_MS = 60_000;
export const ACTIVITY_TICK_MS = 15_000;

/** Foreground intervals only; input after idle never credits the idle gap. */
export class ActivityTracker {
  private previous: number;
  private touched: number;
  private milliseconds = 0;
  private days = new Set<string>();

  constructor(
    private readonly clock: ActivityClock,
    private visible: boolean,
  ) {
    this.previous = clock.monotonic();
    // Entering a visible lesson starts one reading window, not an endless timer.
    this.touched = visible ? this.previous : -Infinity;
  }

  private advance() {
    const now = this.clock.monotonic();
    const wall = this.clock.wallTime();
    if (!Number.isFinite(now) || !Number.isFinite(wall)) return;
    if (now < this.previous) {
      // A broken/injected monotonic clock cannot award duplicate time.
      this.previous = now;
      this.touched = -Infinity;
      return;
    }
    const end = Math.min(now, this.touched + ACTIVITY_IDLE_MS);
    if (this.visible && end > this.previous) {
      this.milliseconds += end - this.previous;
      // An eligible interval is at most 60s and can cross one Shanghai midnight.
      this.days.add(learningDay(wall - (now - this.previous)));
      this.days.add(learningDay(wall - (now - end) - 0.001));
    }
    this.previous = now;
  }

  interact() {
    this.advance();
    if (this.visible) this.touched = this.previous;
  }

  setVisible(visible: boolean) {
    this.advance(); // Settle the visible tail before pausing.
    this.visible = visible;
    // Resuming does not reset the idle deadline without a new interaction.
  }

  flush(): { seconds: number; days: string[] } {
    this.advance();
    const seconds = Math.floor(this.milliseconds / 1000);
    if (!seconds) return { seconds: 0, days: [] };
    this.milliseconds -= seconds * 1000;
    const days = [...this.days];
    this.days.clear();
    return { seconds, days };
  }
}
